import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  useRef,
  ReactNode,
} from "react";
import {
  desktopBridge,
  StatusPayload,
} from "@shared/services/DesktopAgent/DesktopBridge";
import { desktopAgentManager } from "@shared/services/DesktopAgent/DesktopAgentManager";
import { printerService } from "@shared/services/PrinterService/PrinterService";
import {
  DesktopAgentConnectionState,
  PrinterInfo,
} from "@shared/services/DesktopAgent/DesktopAgentTypes";
import { PrintResult } from "@shared/services/PrinterService/PrinterServiceTypes";
import { isPlatform } from "@shared/utils/get-platform";

/**
 * DesktopAgent Context State
 */
export interface DesktopAgentContextValue {
  // Desktop Environment
  /** Whether the desktop shell is ready */
  isDesktopReady: boolean;

  // Connection State
  isConnected: boolean;
  connectionState: DesktopAgentConnectionState;
  reconnectAttempt: number;

  // Printers
  printers: PrinterInfo[];
  refreshPrinters: () => Promise<void>;

  // Printing Methods
  printReceipt: (params: {
    receiptString: string;
    printerName?: string;
    logoUrl?: string;
  }) => Promise<PrintResult>;

  printTickets: (params: {
    ticketItems: unknown[];
    printerName?: string;
    logoUrl?: string;
    pdfUrl?: string;
  }) => Promise<PrintResult>;

  printThermalTickets: (params: {
    pdfUrl: string;
    printerName: string;
  }) => Promise<PrintResult>;

  // Print Status
  isPrinting: boolean;
  lastPrintStatus: PrintResult | null;
  lastPrintError: string | null;

  /** Whether printers are currently being fetched */
  isFetchingPrinters: boolean;

  /** Whether a connection attempt has been made (for loading state) */
  hasAttemptedConnection: boolean;

  /**
   * Ensure connection to the Desktop Agent.
   * Safe to call multiple times - handles all connection states:
   * - Already connected: returns immediately (no-op)
   * - Currently connecting: waits for connection
   * - Not initialized/disconnected: initializes and connects
   */
  ensureConnected: () => Promise<void>;

  /**
   * Force reconnection to the Desktop Agent.
   * Use this when the user explicitly clicks "retry" or "reconnect".
   * This bypasses all state checks and forces a fresh connection,
   * fixing cases where the UI shows disconnected but the internal state is stale.
   */
  forceReconnect: () => Promise<void>;
}

const DesktopAgentContext = createContext<DesktopAgentContextValue | null>(
  null
);

interface DesktopAgentProviderProps {
  children: ReactNode;
}

/**
 * DesktopAgentProvider - Layer 3: React Integration
 *
 * Provides Desktop Agent state and functionality to React components.
 * Only initializes when running inside the Electron desktop app.
 * On non-desktop platforms, renders children without the context provider.
 *
 * Connection is NOT automatic. Components that need Desktop Agent
 * should call `ensureConnected()` when they mount.
 *
 * Lifecycle:
 * 1. Check if running in Electron (skip all initialization if not)
 * 2. Initialize DesktopBridge (sets up event listeners)
 * 3. Wait for components to call ensureConnected()
 * 4. Subscribe to manager and printer service events
 * 5. On unmount, destroy manager and bridge
 */
export function DesktopAgentProvider({
  children,
}: DesktopAgentProviderProps): JSX.Element {
  // Platform guard: Skip all desktop agent logic if not on desktop
  // This prevents unnecessary WebSocket connections, event listeners,
  // and timeouts on web browsers
  const isDesktop = typeof window !== "undefined" && isPlatform("desktop");

  if (!isDesktop) {
    // On non-desktop platforms, render children without the provider
    // useOptionalDesktopAgent() will return null (already handled by consumers)
    // useDesktopAgent() will throw (desktop-only pages should check platform)
    return <>{children}</>;
  }

  return <DesktopAgentProviderInner>{children}</DesktopAgentProviderInner>;
}

/**
 * Inner provider component that contains all the hooks and logic.
 * Only rendered when running inside Electron desktop app.
 */
function DesktopAgentProviderInner({ children }: DesktopAgentProviderProps) {
  // Desktop Environment
  const [isDesktopReady, setIsDesktopReady] = useState(false);

  // Connection State
  const [isConnected, setIsConnected] = useState(false);
  const [connectionState, setConnectionState] =
    useState<DesktopAgentConnectionState>(
      DesktopAgentConnectionState.DISCONNECTED
    );
  const [reconnectAttempt, setReconnectAttempt] = useState(0);

  // Printers
  const [printers, setPrinters] = useState<PrinterInfo[]>([]);
  const [isFetchingPrinters, setIsFetchingPrinters] = useState(
    printerService.isFetchingPrinters
  );

  // Print Status
  const [isPrinting, setIsPrinting] = useState(false);
  const [lastPrintStatus, setLastPrintStatus] = useState<PrintResult | null>(
    null
  );
  const [lastPrintError, setLastPrintError] = useState<string | null>(null);

  // Connection attempt tracking (for loading state)
  const [hasAttemptedConnection, setHasAttemptedConnection] = useState(false);

  // Track if we've already initialized to prevent double-init
  const initializedRef = useRef(false);
  const eventListenersAttachedRef = useRef(false);

  // Event handlers defined outside useEffect for reuse
  const handleConnected = useCallback(() => {
    // eslint-disable-next-line no-console
    console.log("[DesktopAgent] Connected to Desktop Agent");
    setIsConnected(true);
    setIsDesktopReady(true);
    setConnectionState(DesktopAgentConnectionState.CONNECTED);
    setReconnectAttempt(0);
    setLastPrintError(null);
  }, []);

  const handleDisconnected = useCallback(() => {
    // eslint-disable-next-line no-console
    console.log("[DesktopAgent] Disconnected from Desktop Agent");
    setIsConnected(false);
    setConnectionState(DesktopAgentConnectionState.DISCONNECTED);
  }, []);

  const handleReconnecting = useCallback(({ attempt }: { attempt: number }) => {
    // eslint-disable-next-line no-console
    console.log(`[DesktopAgent] Reconnecting... (attempt ${attempt})`);
    setConnectionState(DesktopAgentConnectionState.RECONNECTING);
    setReconnectAttempt(attempt);
  }, []);

  const handleError = useCallback((error: Error) => {
    // eslint-disable-next-line no-console
    console.warn("[DesktopAgent] Error:", error.message);
    setConnectionState(DesktopAgentConnectionState.ERROR);
    setLastPrintError(error.message);
  }, []);

  // Attach event listeners to manager (safe to call multiple times)
  const attachEventListeners = useCallback(() => {
    if (eventListenersAttachedRef.current) return;

    desktopAgentManager.on("connected", handleConnected);
    desktopAgentManager.on("disconnected", handleDisconnected);
    desktopAgentManager.on("reconnecting", handleReconnecting);
    desktopAgentManager.on("error", handleError);

    eventListenersAttachedRef.current = true;
  }, [handleConnected, handleDisconnected, handleReconnecting, handleError]);

  /**
   * Ensure connection to the Desktop Agent
   * This is the primary method for components to request a connection.
   * Safe to call multiple times - redundant calls are handled gracefully.
   */
  const ensureConnected = useCallback(async () => {
    // SSR guard
    if (typeof window === "undefined") return;

    // eslint-disable-next-line no-console
    console.log("[DesktopAgent] ensureConnected called");

    // Attach listeners before connecting
    attachEventListeners();

    // If already connected, just mark as attempted and return
    if (desktopAgentManager.isConnected()) {
      // eslint-disable-next-line no-console
      console.log(
        "[DesktopAgent] Already connected, skipping connection attempt"
      );
      setHasAttemptedConnection(true);
      return;
    }

    // Update state to show we're attempting to connect
    setConnectionState(DesktopAgentConnectionState.CONNECTING);

    try {
      await desktopAgentManager.ensureConnected();
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error("[DesktopAgent] Failed to connect:", err);
      setConnectionState(DesktopAgentConnectionState.ERROR);
      if (err instanceof Error) {
        setLastPrintError(err.message);
      }
    } finally {
      // Mark that we've attempted connection (for loading state)
      setHasAttemptedConnection(true);
    }
  }, [attachEventListeners]);

  /**
   * Force reconnection to the Desktop Agent
   * Use this when the user explicitly clicks "retry" or "reconnect".
   * This bypasses all state checks and forces a fresh connection.
   */
  const forceReconnect = useCallback(async () => {
    // SSR guard
    if (typeof window === "undefined") return;

    // eslint-disable-next-line no-console
    console.log(
      "[DesktopAgent] forceReconnect called - bypassing state checks"
    );

    // Attach listeners before connecting
    attachEventListeners();

    // Update state to show we're attempting to connect
    setConnectionState(DesktopAgentConnectionState.CONNECTING);
    setLastPrintError(null);

    try {
      await desktopAgentManager.forceReconnect();
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error("[DesktopAgent] Force reconnect failed:", err);
      setConnectionState(DesktopAgentConnectionState.ERROR);
      if (err instanceof Error) {
        setLastPrintError(err.message);
      }
    } finally {
      setHasAttemptedConnection(true);
    }
  }, [attachEventListeners]);

  // Initialize bridge and set up desktop ready listener
  useEffect(() => {
    // SSR guard
    if (typeof window === "undefined") return undefined;

    // Prevent double initialization in StrictMode
    if (initializedRef.current) return undefined;
    initializedRef.current = true;

    // eslint-disable-next-line no-console
    console.log("[DesktopAgent] Provider initializing...");

    // Handler for desktop ready event (from Electron shell)
    const handleDesktopReady = (payload: StatusPayload) => {
      // eslint-disable-next-line no-console
      console.log("[DesktopAgent] Desktop ready signal received:", payload);
      setIsDesktopReady(true);
      // Auto-connect when desktop signals it's ready
      ensureConnected();
    };

    // Initialize bridge - sets up listeners and queries desktop
    desktopBridge.initialize();

    // Subscribe to ready events
    const unsubscribeReady = desktopBridge.subscribe<StatusPayload>(
      "ready",
      handleDesktopReady
    );

    const unsubscribeDesktopReady = desktopBridge.subscribe<StatusPayload>(
      "desktop:ready",
      handleDesktopReady
    );

    // Cleanup on unmount
    return () => {
      unsubscribeReady();
      unsubscribeDesktopReady();

      // Remove manager event listeners
      if (eventListenersAttachedRef.current) {
        desktopAgentManager.off("connected", handleConnected);
        desktopAgentManager.off("disconnected", handleDisconnected);
        desktopAgentManager.off("reconnecting", handleReconnecting);
        desktopAgentManager.off("error", handleError);
        eventListenersAttachedRef.current = false;
      }

      // Destroy manager and bridge
      desktopAgentManager.destroy();
      desktopBridge.destroy();

      initializedRef.current = false;
    };
  }, [
    ensureConnected,
    handleConnected,
    handleDisconnected,
    handleReconnecting,
    handleError,
  ]);

  // Subscribe to PrinterService events
  useEffect(() => {
    const handlePrintersChanged = (updatedPrinters: PrinterInfo[]) => {
      // eslint-disable-next-line no-console
      console.log(
        `[DesktopAgent] Printers updated: ${updatedPrinters.length} found`
      );
      setPrinters(updatedPrinters);
    };

    const handlePrintersLoading = (loading: boolean) => {
      setIsFetchingPrinters(loading);
    };

    const handlePrintStart = () => {
      // eslint-disable-next-line no-console
      console.log("[DesktopAgent] Print job started");
      setIsPrinting(true);
      setLastPrintStatus(null);
      setLastPrintError(null);
    };

    const handlePrintSuccess = ({
      usedFallback,
    }: {
      usedFallback: boolean;
    }) => {
      // eslint-disable-next-line no-console
      console.log(
        `[DesktopAgent] Print job succeeded${
          usedFallback ? " (using browser fallback)" : ""
        }`
      );
      setIsPrinting(false);
      setLastPrintStatus({ success: true, usedFallback });
      setLastPrintError(null);
    };

    const handlePrintError = ({
      error,
      type,
    }: {
      error: string;
      type: string;
    }) => {
      // eslint-disable-next-line no-console
      console.error("[DesktopAgent] Print job failed:", {
        type,
        error,
        timestamp: new Date().toISOString(),
      });
      setIsPrinting(false);
      setLastPrintStatus({ success: false, error });
      setLastPrintError(error);
    };

    // Subscribe to printer service events
    printerService.on("printers-changed", handlePrintersChanged);
    printerService.on("printers-loading", handlePrintersLoading);
    printerService.on("print-start", handlePrintStart);
    printerService.on("print-success", handlePrintSuccess);
    printerService.on("print-error", handlePrintError);

    // Set initial printer state
    setPrinters(printerService.getCachedPrinters());

    return () => {
      printerService.off("printers-changed", handlePrintersChanged);
      printerService.off("printers-loading", handlePrintersLoading);
      printerService.off("print-start", handlePrintStart);
      printerService.off("print-success", handlePrintSuccess);
      printerService.off("print-error", handlePrintError);
    };
  }, []);

  // Refresh printers
  const refreshPrinters = useCallback(async () => {
    // eslint-disable-next-line no-console
    console.log("[DesktopAgent] Refreshing printers...");
    await printerService.getPrinters();
  }, []);

  // Print methods - delegate to PrinterService
  const printReceipt = useCallback(
    async (params: {
      receiptString: string;
      printerName?: string;
      logoUrl?: string;
    }): Promise<PrintResult> => {
      return printerService.printReceipt(params);
    },
    []
  );

  const printTickets = useCallback(
    async (params: {
      ticketItems: unknown[];
      printerName?: string;
      logoUrl?: string;
      pdfUrl?: string;
    }): Promise<PrintResult> => {
      return printerService.printTickets(params);
    },
    []
  );

  const printThermalTickets = useCallback(
    async (params: {
      pdfUrl: string;
      printerName: string;
    }): Promise<PrintResult> => {
      return printerService.printThermalTickets(params);
    },
    []
  );

  const value: DesktopAgentContextValue = {
    // Desktop Environment
    isDesktopReady,

    // Connection
    isConnected,
    connectionState,
    reconnectAttempt,
    hasAttemptedConnection,

    // Printers
    printers,
    isFetchingPrinters,
    refreshPrinters,

    // Printing
    printReceipt,
    printTickets,
    printThermalTickets,

    // Status
    isPrinting,
    lastPrintStatus,
    lastPrintError,

    // Connection control
    ensureConnected,
    forceReconnect,
  };

  return (
    <DesktopAgentContext.Provider value={value}>
      {children}
    </DesktopAgentContext.Provider>
  );
}

/**
 * useDesktopAgent - Hook to access Desktop Agent functionality
 *
 * @returns DesktopAgentContextValue
 * @throws Error if used outside of DesktopAgentProvider
 */
export function useDesktopAgent(): DesktopAgentContextValue {
  const context = useContext(DesktopAgentContext);

  if (!context) {
    throw new Error(
      "useDesktopAgent must be used within a DesktopAgentProvider"
    );
  }

  return context;
}

/**
 * useOptionalDesktopAgent - Hook to safely access Desktop Agent functionality
 *
 * Use this hook when the component may be rendered outside of a DesktopAgentProvider.
 * Returns null if not within a provider, allowing safe optional chaining.
 *
 * @returns DesktopAgentContextValue | null
 */
export function useOptionalDesktopAgent(): DesktopAgentContextValue | null {
  return useContext(DesktopAgentContext);
}
